import { Plus, Trash2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { SkillsInput } from "./SkillsInput";
import { AiButton, SummaryAssist } from "./AiAssist";

const Field = ({ label, id, value, onChange, area, placeholder }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-xs text-slate-400">{label}</Label>
    {area ? (
      <Textarea id={id} maxLength={3000} data-testid={`field-${id}`} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} rows={4} className="bg-[#0c0e14] border-white/10 placeholder:text-slate-600 focus-visible:ring-emerald-500" />
    ) : (
      <Input id={id} maxLength={200} data-testid={`field-${id}`} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="bg-[#0c0e14] border-white/10 placeholder:text-slate-600 focus-visible:ring-emerald-500" />
    )}
  </div>
);

const LIST_FIELDS = {
  experience: [["role", "Job title"], ["company", "Company"], ["start", "Start"], ["end", "End"], ["description", "What you did", true]],
  education: [["degree", "Degree"], ["school", "School"], ["start", "Start"], ["end", "End"], ["details", "Details", true]],
  projects: [["name", "Project name"], ["link", "Link"], ["start", "Start"], ["end", "End"], ["description", "Description", true]],
};

const MAX_ITEMS = { experience: 20, education: 10, projects: 15 };

const ListSection = ({ kind, items, onChange, resumeId, patch }) => {
  const fields = LIST_FIELDS[kind];
  const blank = Object.fromEntries(fields.map(([k]) => [k, ""]));
  const update = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const setDesc = (i, t) => patch((d) => ({ ...d, experience: d.experience.map((it, j) => (j === i ? { ...it, description: t } : it)) }));
  return (
    <div className="space-y-5">
      {items.map((it, i) => (
        <div key={i} className="rounded-xl border border-white/10 p-5 space-y-4 bg-white/[0.02]" data-testid={`${kind}-item-${i}`}>
          <div className="flex justify-between items-center">
            <span className="text-xs font-mono uppercase tracking-widest text-slate-500">#{i + 1}</span>
            <div className="flex items-center gap-3">
              {kind === "experience" && (
                <AiButton testId={`experience-ai-btn-${i}`} label="AI bullets" previous={it.description} onText={(t) => setDesc(i, t)}
                  body={() => ({ kind: "bullets", resume_id: resumeId, item_index: i, role: it.role, company: it.company, notes: it.description })} />
              )}
              <button onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${kind}-remove-${i}`} className="text-slate-500 hover:text-red-400 transition-colors" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {fields.map(([k, label, area]) => (
              <div key={k} className={area || k === fields[0][0] || k === fields[1][0] ? "col-span-2" : ""}>
                <Field id={`${kind}-${i}-${k}`} label={label} area={area} value={it[k] || ""} onChange={(v) => update(i, k, v)} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={() => onChange([...items, blank])} disabled={items.length >= MAX_ITEMS[kind]} data-testid={`${kind}-add-btn`} className="disabled:opacity-40 disabled:cursor-not-allowed w-full rounded-xl border border-dashed border-white/15 py-3 text-sm text-slate-400 hover:text-emerald-400 hover:border-emerald-500/40 transition-colors flex items-center justify-center gap-2">
        <Plus className="h-4 w-4" /> Add {kind === "experience" ? "position" : kind === "education" ? "education" : "project"}
      </button>
    </div>
  );
};

const PERSONAL = [["full_name", "Full name", "e.g. Aisha Kapoor"], ["headline", "Headline", "e.g. Senior Product Designer"], ["email", "Email"], ["phone", "Phone"], ["location", "Location"], ["website", "Website / LinkedIn"]];

export const ResumeForm = ({ data, onChange, resumeId }) => {
  const set = (key, value) => onChange({ ...data, [key]: value });
  const tabs = ["personal", "summary", "experience", "education", "projects", "skills"];
  return (
    <Tabs defaultValue="personal" className="w-full">
      <TabsList className="w-full flex flex-wrap h-auto bg-[#0c0e14] border border-white/10 p-1 rounded-xl">
        {tabs.map((t) => (
          <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="flex-1 capitalize text-xs rounded-lg data-[state=active]:bg-emerald-500 data-[state=active]:text-black">{t}</TabsTrigger>
        ))}
      </TabsList>
      <TabsContent value="personal" className="mt-6 grid grid-cols-2 gap-4">
        {PERSONAL.map(([k, label, ph]) => (
          <div key={k} className={k === "full_name" || k === "headline" ? "col-span-2" : ""}>
            <Field id={k} label={label} placeholder={ph} value={data.personal[k] || ""} onChange={(v) => set("personal", { ...data.personal, [k]: v })} />
          </div>
        ))}
      </TabsContent>
      <TabsContent value="summary" className="mt-6">
        <Field id="summary" label="Professional summary" area value={data.summary} onChange={(v) => set("summary", v)} placeholder="2–3 sentences about who you are and what you do best." />
        <SummaryAssist resumeId={resumeId} previous={data.summary} onText={(t) => onChange((d) => ({ ...d, summary: t }))} />
      </TabsContent>
      {["experience", "education", "projects"].map((k) => (
        <TabsContent key={k} value={k} className="mt-6"><ListSection kind={k} resumeId={resumeId} patch={onChange} items={data[k]} onChange={(v) => set(k, v)} /></TabsContent>
      ))}
      <TabsContent value="skills" className="mt-6"><SkillsInput skills={data.skills} onChange={(v) => set("skills", v)} /></TabsContent>
    </Tabs>
  );
};
